import { useState } from "react";
import { useLocation } from "react-router-dom";
import { MessageCircle, Bug, Lightbulb, X } from "lucide-react";
import { toast } from "sonner";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { useFeedback } from "../context/FeedbackContext";
import { useAuth } from "../context/AuthContext";

/**
 * Floating Feedback Launcher — visible only when Feedback Mode is active.
 *
 * UX:
 *   • Small pill at the bottom-right with a chat-bubble icon and pending
 *     count. Click → opens a two-tab dialog (Issue / Idea) with a textarea
 *     and a transparent "What we capture" block so users know exactly what's
 *     being saved.
 *   • Submitting stores locally; an admin can review at /admin/feedback.
 */
export default function FeedbackLauncher() {
    const { feedbackMode, addFeedback, pendingCount, setFeedbackMode } =
        useFeedback();
    const { currentUser, workspaceMode } = useAuth();
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [kind, setKind] = useState("issue");
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");

    if (!feedbackMode) return null;

    const reset = () => {
        setTitle("");
        setBody("");
        setKind("issue");
    };

    const submit = (e) => {
        e?.preventDefault?.();
        try {
            const entry = addFeedback({
                kind,
                title,
                body,
                page: location.pathname,
            });
            toast.success(
                kind === "issue" ? "Issue reported" : "Suggestion captured",
                {
                    description:
                        "Thanks — saved locally. An admin can review under Studio Admin → Feedback.",
                },
            );
            reset();
            setOpen(false);
            return entry;
        } catch (err) {
            toast.error(err.message || "Could not save feedback");
            return null;
        }
    };

    return (
        <>
            <div
                className="fixed bottom-20 right-5 z-50 flex items-center gap-2"
                data-testid="feedback-launcher"
            >
                <button
                    type="button"
                    onClick={() => setFeedbackMode(false)}
                    className="focus-ring h-9 w-9 rounded-full bg-white border border-mist text-slate-400 hover:text-slate-700 hover:bg-mist/50 shadow-sm inline-flex items-center justify-center"
                    aria-label="Turn off Feedback Mode"
                    title="Turn off Feedback Mode"
                    data-testid="feedback-disable-btn"
                >
                    <X size={14} strokeWidth={2.8} />
                </button>
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="focus-ring inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand/90 text-white font-medium h-11 px-5 shadow-lift"
                    data-testid="feedback-open-btn"
                >
                    <MessageCircle size={16} strokeWidth={2.4} />
                    Feedback
                    {pendingCount > 0 && (
                        <span
                            className="ml-1 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-action text-[10px] font-semibold tabular-nums"
                            data-testid="feedback-pending-count"
                        >
                            {pendingCount}
                        </span>
                    )}
                </button>
            </div>

            <Dialog
                open={open}
                onOpenChange={(v) => {
                    setOpen(v);
                    if (!v) reset();
                }}
            >
                <DialogContent
                    className="rounded-3xl max-w-lg"
                    data-testid="feedback-dialog"
                >
                    <DialogHeader>
                        <DialogTitle className="font-display uppercase tracking-tight">
                            Send Feedback
                        </DialogTitle>
                        <DialogDescription>
                            Help us improve Tempo based on real daily use.
                        </DialogDescription>
                    </DialogHeader>

                    {/* Kind tabs */}
                    <div
                        className="grid grid-cols-2 gap-2"
                        data-testid="feedback-kind-tabs"
                    >
                        <button
                            type="button"
                            onClick={() => setKind("issue")}
                            data-testid="feedback-kind-issue"
                            aria-pressed={kind === "issue"}
                            className={`focus-ring rounded-xl border-2 px-3 py-3 text-left transition-all ${
                                kind === "issue"
                                    ? "bg-danger-soft border-danger/30 text-danger"
                                    : "bg-mist/50 border-mist text-slate-600 hover:bg-mist"
                            }`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Bug size={15} strokeWidth={2.8} />
                                <span className="font-bold text-sm">Report Issue</span>
                            </div>
                            <div className="text-xs opacity-80">
                                Broken, confusing, or slow.
                            </div>
                        </button>
                        <button
                            type="button"
                            onClick={() => setKind("idea")}
                            data-testid="feedback-kind-idea"
                            aria-pressed={kind === "idea"}
                            className={`focus-ring rounded-xl border-2 px-3 py-3 text-left transition-all ${
                                kind === "idea"
                                    ? "bg-warning-soft border-warning/30 text-warning"
                                    : "bg-mist/50 border-mist text-slate-600 hover:bg-mist"
                            }`}
                        >
                            <div className="flex items-center gap-2 mb-1">
                                <Lightbulb size={15} strokeWidth={2.8} />
                                <span className="font-bold text-sm">
                                    Suggest Improvement
                                </span>
                            </div>
                            <div className="text-xs opacity-80">
                                What would make teaching easier?
                            </div>
                        </button>
                    </div>

                    <form onSubmit={submit} className="space-y-4 mt-2">
                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                Title (optional)
                            </Label>
                            <Input
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                maxLength={80}
                                placeholder={
                                    kind === "issue"
                                        ? "e.g. Attendance button skipped after Undo"
                                        : "e.g. Quick log all 4 students at once"
                                }
                                className="h-11 rounded-xl bg-mist/50 border-mist"
                                data-testid="feedback-title"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs font-bold uppercase tracking-wider text-slate-600">
                                What happened?{" "}
                                <span className="text-danger">*</span>
                            </Label>
                            <Textarea
                                value={body}
                                onChange={(e) => setBody(e.target.value)}
                                placeholder="Describe what you saw or what would help…"
                                rows={5}
                                className="rounded-xl bg-mist/50 border-mist min-h-[120px] focus-visible:ring-[#3D6385]"
                                data-testid="feedback-body"
                                autoFocus
                                required
                            />
                        </div>

                        <details
                            className="text-xs text-slate-500 bg-mist/50 rounded-xl p-3"
                            data-testid="feedback-context-disclosure"
                        >
                            <summary className="cursor-pointer font-bold text-slate-700">
                                What we capture (transparent)
                            </summary>
                            <ul className="mt-2 space-y-1">
                                <li>
                                    <b>Page</b>:{" "}
                                    <code className="bg-white px-1 rounded">
                                        {location.pathname}
                                    </code>
                                </li>
                                <li>
                                    <b>Teacher</b>:{" "}
                                    {currentUser?.name || "—"} ({currentUser?.role || "—"})
                                </li>
                                <li>
                                    <b>Workspace</b>: {workspaceMode || "all"}
                                </li>
                                <li>
                                    <b>Time</b>: now (ISO timestamp)
                                </li>
                                <li>
                                    <b>Browser</b>: navigator.userAgent + viewport size
                                </li>
                            </ul>
                            <p className="mt-2 italic">
                                Saved locally only. Nothing leaves this device until a
                                future Supabase sync is wired up.
                            </p>
                        </details>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => setOpen(false)}
                                className="rounded-xl font-bold"
                                data-testid="feedback-cancel-btn"
                            >
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                disabled={!body.trim()}
                                className="rounded-xl bg-[#3D6385] hover:bg-[#345570] text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed"
                                data-testid="feedback-submit-btn"
                            >
                                Send
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>
        </>
    );
}
